import { Component, HostListener, OnInit, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd, RouterLinkActive, RouterLink } from '@angular/router';
import { SidebarService } from '@shared/services/sidebar.service';
import { trigger, style, transition, animate } from '@angular/animations';
import { filter } from 'rxjs';
import { AuthService } from '@shared/services/auth.service';
import { MenuService } from '@shared/services/menu.service';
import { buildMenuItems, MenuItem } from '@core/layout/sidebar/sidebar-menu.config';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

export type { MenuItem } from '@core/layout/sidebar/sidebar-menu.config';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ height: 0, opacity: 0 }),
                animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in-out', style({ height: 0, opacity: 0 }))
            ])
        ])
    ],
    imports: [RouterLinkActive, RouterLink, MatIcon, TranslatePipe]
})
export class SidebarComponent implements OnInit {
  public sidebarService = inject(SidebarService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private menuService = inject(MenuService);

  private readonly menu = toSignal(this.menuService.menu$, { initialValue: {} });

  readonly menuItems = computed<MenuItem[]>(() => buildMenuItems(this.menu()));

  openGroups: { [key: string]: boolean } = {};

  readonly MOBILE_BREAKPOINT = 992;

  constructor() {
    effect(() => {
      for (const item of this.menuItems()) {
        if (item.key && this.openGroups[item.key] === undefined) {
          this.openGroups[item.key] = false;
        }
      }
    });
  }

  ngOnInit() {
    if (window.innerWidth < this.MOBILE_BREAKPOINT) {
      this.sidebarService.close();
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (window.innerWidth < this.MOBILE_BREAKPOINT) {
          this.sidebarService.close();
        }
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    if (window.innerWidth < this.MOBILE_BREAKPOINT) {
      this.sidebarService.close();
    }
  }

  logout() {
    const refreshToken = this.authService.getRefreshToken();

    this.authService.logout(refreshToken).subscribe({
      next: () => this.clearSession(),
      error: () => this.clearSession()
    });
  }

  private clearSession() {
    this.authService.clearSession();
    this.router.navigate(['/login']);
  }

  toggleGroup(item: MenuItem) {
    if (item.key) {
      this.openGroups[item.key] = !this.openGroups[item.key];
    }
  }

  isGroupOpen(item: MenuItem): boolean {
    return item.key ? !!this.openGroups[item.key] : false;
  }
}
